import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { arrayHasItems } from '../../helpers/common';
import { toast } from 'react-toastify';

const safeString = (v) => (v === null || v === undefined ? '' : String(v));
const isPreviousYearToken = (token) => Boolean(token?.is_previous_year ?? token?.isPreviousYear);
const appendPreviousYearLabel = (label, isPreviousYear) => {
  const safeLabel = safeString(label).trim();
  if (!safeLabel) return safeLabel;
  return isPreviousYear ? `${safeLabel} (PY)` : safeLabel;
};
const formatBudgetCodeLabel = (code, name) => {
  const safeCode = safeString(code).trim();
  const safeName = safeString(name).trim();
  if (!safeCode) return safeName;
  if (!safeName || safeName === safeCode) return safeCode;
  return `${safeCode} - ${safeName}`;
};

const isMathOperator = (op) => ['+', '-', '*', '/'].includes(op);
const isDigitKey = (key) => /^[0-9]$/.test(key);
const isNumberToken = (token) => token?.type === 'number';
const isFormulaBoundaryCharacter = (char) => !char || /\s/.test(char) || ['+', '-', '*', '/', '(', ')'].includes(char);

// Token normalization for P&L calculation
const normalizePLTokens = ({ tokens, plKeysById }) => {
  const arr = arrayHasItems(tokens) ? tokens : [];
  return arr
    .map((t) => {
      if (!t || typeof t !== 'object') return null;
      const type = safeString(t.type).trim();
      if (type === 'pl_key') {
        const plId = t.plId ?? t.pl_id ?? null;
        if (!plId) return null;
        const plName = safeString(
          t.plName ?? t.pl_name ?? plKeysById[String(plId)]?.name ?? ''
        );
        return { type: 'pl_key', plId, plName, is_previous_year: isPreviousYearToken(t) };
      }
      if (type === 'operator') {
        const value = safeString(t.value).trim();
        if (!value) return null;
        return { type: 'operator', value };
      }
      if (type === 'number') {
        const value = safeString(t.value);
        if (!value) return null;
        return { type: 'number', value };
      }
      return null;
    })
    .filter(Boolean);
};

// Token normalization for Budget calculation
const normalizeBudgetTokens = ({ tokens, budgetItemsByCode, budgetCategoriesById }) => {
  const arr = arrayHasItems(tokens) ? tokens : [];
  return arr
    .map((t) => {
      if (!t || typeof t !== 'object') return null;
      const type = safeString(t.type).trim();
      if (type === 'budget_category') {
        const categoryId = t.categoryId ?? t.category_id ?? null;
        if (!categoryId) return null;
        const categoryName = safeString(
          t.categoryName ?? t.category_name ?? budgetCategoriesById[String(categoryId)] ?? ''
        );
        return { type: 'budget_category', categoryId, categoryName, is_previous_year: isPreviousYearToken(t) };
      }
      if (type === 'budget_code') {
        const code = safeString(t.code).trim();
        if (!code) return null;
        const name = safeString(
          t.name ?? budgetItemsByCode[code]?.name ?? code
        );
        return { type: 'budget_code', code, name, is_previous_year: isPreviousYearToken(t) };
      }
      if (type === 'operator') {
        const value = safeString(t.value).trim();
        if (!value) return null;
        return { type: 'operator', value };
      }
      if (type === 'number') {
        const value = safeString(t.value);
        if (!value) return null;
        return { type: 'number', value };
      }
      return null;
    })
    .filter(Boolean);
};

const tokenToText = (t, budgetItemsByCode = {}) => {
  if (t?.type === 'operator') return t.value;
  if (t?.type === 'number') return safeString(t.value);
  if (t?.type === 'pl_key') return appendPreviousYearLabel(t.plName || `PL_${t.plId}`, isPreviousYearToken(t));
  if (t?.type === 'budget_category') return appendPreviousYearLabel(t.categoryName || `Category_${t.categoryId}`, isPreviousYearToken(t));
  if (t?.type === 'budget_code') {
    const name = t.name || budgetItemsByCode[t.code]?.name || t.code;
    return appendPreviousYearLabel(formatBudgetCodeLabel(t.code, name), isPreviousYearToken(t));
  }
  return '';
};

const getTextareaTokenClasses = (token, isInvalid) => {
  if (isInvalid) {
    return 'bg-red-50 border border-red-200 text-red-600';
  }
  if (token?.type === 'pl_key') {
    return 'bg-blue-50 border border-blue-200 text-blue-700';
  }
  if (token?.type === 'budget_code') {
    return 'bg-green-50 border border-green-200 text-green-700';
  }
  if (token?.type === 'budget_category') {
    return 'bg-purple-50 border border-purple-200 text-purple-700';
  }
  if (token?.type === 'operator') {
    if (token?.value === '(' || token?.value === ')') {
      return 'bg-gray-100 border border-gray-200 text-primary font-extrabold text-base';
    }
    return 'bg-gray-100 border border-gray-200 text-gray-700';
  }
  if (token?.type === 'number') {
    return 'bg-amber-50 border border-amber-200 text-amber-700';
  }
  return 'bg-gray-50 border border-gray-200 text-gray-700';
};

const sectionCardClassName = 'rounded-xl border border-gray-200 bg-white/90 p-4 shadow-sm';

const getTokenKind = (t) => {
  if (!t || typeof t !== 'object') return 'unknown';
  if (t.type === 'pl_key' || t.type === 'budget_code' || t.type === 'budget_category' || t.type === 'number') return 'value';
  if (t.type === 'operator') {
    const v = safeString(t.value).trim();
    if (v === '(') return 'lparen';
    if (v === ')') return 'rparen';
    if (isMathOperator(v)) return 'op';
    return 'unknown';
  }
  return 'unknown';
};

const validateTokens = (tokens, calculationType, budgetItemsByCode = {}) => {
  const arr = arrayHasItems(tokens) ? tokens : [];
  let balance = 0;
  let prev = 'start';
  let hasValue = false;

  for (const t of arr) {
    const kind = getTokenKind(t);

    if (kind === 'value') {
      hasValue = true;
      // Check if budget code exists
      if (calculationType === 'budget' && t.type === 'budget_code') {
        const code = safeString(t.code).trim();
        if (!budgetItemsByCode[code]) {
          return { isValid: false, reason: `Code "${code}" does not exist in any budget.`, invalidCode: code };
        }
      }
      if (prev === 'value') return { isValid: false, reason: 'Missing operator between values.' };
      prev = 'value';
      continue;
    }

    if (kind === 'lparen') {
      if (prev === 'value') return { isValid: false, reason: 'Missing operator before "(".' };
      balance += 1;
      prev = 'lparen';
      continue;
    }

    if (kind === 'rparen') {
      if (prev !== 'value') return { isValid: false, reason: '")" must come after a value.' };
      balance -= 1;
      if (balance < 0) return { isValid: false, reason: 'Unmatched ")" in formula.' };
      prev = 'value';
      continue;
    }

    if (kind === 'op') {
      const op = safeString(t.value).trim();
      if (op === '-' && (prev === 'start' || prev === 'op' || prev === 'lparen')) {
        prev = 'op';
        continue;
      }
      if (prev !== 'value') return { isValid: false, reason: 'Operator must come after a value.' };
      prev = 'op';
      continue;
    }

    return { isValid: false, reason: 'Unknown token in formula.' };
  }

  if (!hasValue) return { isValid: false, reason: 'Formula must include at least one value.' };
  if (balance !== 0) return { isValid: false, reason: 'Unclosed "(" in formula.' };
  if (prev === 'op' || prev === 'lparen') return { isValid: false, reason: 'Formula cannot end with an operator.' };

  return { isValid: true, reason: '' };
};

const getProfitAndLossWarningMessage = (invalidCount) => {
  if (!invalidCount) return '';
  return invalidCount === 1
    ? 'This P&L item has no current data and will be treated as 0.'
    : 'These P&L items have no current data and will be treated as 0.';
};

export const TableItemCreateEditSidebar = ({
  embedded = false,
  isOpen,
  onClose,
  mode = 'create',
  item,
  profitAndLossKeys = [],
  budgetLineItems = [],
  categoriesToRender = [],
  onSave,
  isSaving = false,
  onRegisterSubmit,
  onCanSaveChange,
}) => {
  const initialSnapshotRef = useRef('');
  const submitRef = useRef(null);
  const textareaRef = useRef(null);
  const initializedItemIdRef = useRef(null);

  const [calculationType, setCalculationType] = useState('pl'); // 'pl' | 'budget'
  const [cursorPosition, setCursorPosition] = useState(0);
  const [plSearch, setPlSearch] = useState('');
  const [budgetLineItemSearch, setBudgetLineItemSearch] = useState('');
  const [isPreviousYearEnabled, setIsPreviousYearEnabled] = useState(false);
  const [pasteError, setPasteError] = useState('');

  const plKeysById = useMemo(() => {
    const map = {};
    (arrayHasItems(profitAndLossKeys) ? profitAndLossKeys : []).forEach((k) => {
      if (k?.pl_id) {
        map[String(k.pl_id)] = {
          name: k?.name || '',
          is_new: Boolean(k?.is_new),
        };
      }
    });
    return map;
  }, [profitAndLossKeys]);

  const activePlKeyIds = useMemo(() => {
    const ids = new Set();
    Object.entries(plKeysById).forEach(([plId, plKey]) => {
      if (plKey?.is_new) ids.add(plId);
    });
    return ids;
  }, [plKeysById]);

  const budgetItemsByCode = useMemo(() => {
    const map = {};
    (arrayHasItems(budgetLineItems) ? budgetLineItems : []).forEach((item) => {
      const code = safeString(item.account_number).trim();
      if (code) {
        map[code] = {
          code,
          name: item.account_name || code,
        };
      }
    });
    return map;
  }, [budgetLineItems]);

  const budgetCategoriesById = useMemo(() => {
    const map = {};
    (arrayHasItems(categoriesToRender) ? categoriesToRender : []).forEach((category) => {
      if (!category?.id) return;
      map[String(category.id)] = category?.name || '';
    });
    return map;
  }, [categoriesToRender]);

  const filteredProfitAndLossKeys = useMemo(() => {
    const query = safeString(plSearch).trim().toLowerCase();
    const items = arrayHasItems(profitAndLossKeys) ? profitAndLossKeys : [];
    if (!query) return items;
    return items.filter((item) => {
      const name = safeString(item?.name).toLowerCase();
      const id = safeString(item?.pl_id).toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [profitAndLossKeys, plSearch]);

  const filteredBudgetLineItems = useMemo(() => {
    const query = safeString(budgetLineItemSearch).trim().toLowerCase();
    const items = Object.values(budgetItemsByCode);
    if (!query) return items;
    return items.filter((item) => {
      const name = safeString(item?.name).toLowerCase();
      const code = safeString(item?.code).toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [budgetItemsByCode, budgetLineItemSearch]);

  const plPasteCandidates = useMemo(() => {
    const items = arrayHasItems(profitAndLossKeys) ? profitAndLossKeys : [];
    const seen = new Set();

    return items
      .flatMap((plKey) => {
        const plId = safeString(plKey?.pl_id).trim();
        if (!plId) return [];
        const plName = safeString(plKey?.name).trim();
        return [
          { label: plName, token: { type: 'pl_key', plId, plName } },
          { label: appendPreviousYearLabel(plName, true), token: { type: 'pl_key', plId, plName, is_previous_year: true } },
          { label: plId, token: { type: 'pl_key', plId, plName } },
          { label: `PL_${plId}`, token: { type: 'pl_key', plId, plName } },
        ];
      })
      .filter((entry) => {
        const label = safeString(entry.label).trim().toLowerCase();
        if (!label || seen.has(label)) return false;
        seen.add(label);
        return true;
      })
      .sort((a, b) => b.label.length - a.label.length);
  }, [profitAndLossKeys]);

  const budgetPasteCandidates = useMemo(() => {
    const categoryNameSet = new Set(
      (arrayHasItems(categoriesToRender) ? categoriesToRender : [])
        .map((category) => safeString(category?.name).trim().toLowerCase())
        .filter(Boolean)
    );

    const lineItemNameCounts = Object.values(budgetItemsByCode).reduce((acc, item) => {
      const name = safeString(item?.name).trim().toLowerCase();
      if (!name) return acc;
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    const lineItems = Object.values(budgetItemsByCode).flatMap((item) => {
      const code = safeString(item?.code).trim();
      if (!code) return [];
      const name = safeString(item?.name).trim() || code;
      const normalizedName = name.toLowerCase();
      const candidates = [
        { label: formatBudgetCodeLabel(code, name), token: { type: 'budget_code', code, name } },
        { label: appendPreviousYearLabel(formatBudgetCodeLabel(code, name), true), token: { type: 'budget_code', code, name, is_previous_year: true } },
        { label: code, token: { type: 'budget_code', code, name } },
      ];

      if (!categoryNameSet.has(normalizedName) && lineItemNameCounts[normalizedName] === 1) {
        candidates.push({ label: name, token: { type: 'budget_code', code, name } });
        candidates.push({ label: appendPreviousYearLabel(name, true), token: { type: 'budget_code', code, name, is_previous_year: true } });
      }

      return candidates;
    });

    const categories = (arrayHasItems(categoriesToRender) ? categoriesToRender : []).flatMap((category) => {
      const categoryId = category?.id;
      if (!categoryId) return [];
      const categoryName = safeString(category?.name).trim();
      return [
        { label: categoryName, token: { type: 'budget_category', categoryId, categoryName } },
        { label: appendPreviousYearLabel(categoryName, true), token: { type: 'budget_category', categoryId, categoryName, is_previous_year: true } },
        { label: `Category_${categoryId}`, token: { type: 'budget_category', categoryId, categoryName } },
      ];
    });

    const seen = new Set();
    return [...categories, ...lineItems]
      .filter((entry) => {
        const label = safeString(entry.label).trim().toLowerCase();
        if (!label || seen.has(label)) return false;
        seen.add(label);
        return true;
      })
      .sort((a, b) => b.label.length - a.label.length);
  }, [budgetItemsByCode, categoriesToRender]);

  const buildInitialState = useMemo(() => {
    if (mode === 'edit' && item) {
      const itemData = item?.item || {};
      const plCalculation = item?.calculation_profit_and_loss;
      const budgetCalculation = item?.calculation_budget;

      // Determine calculation type
      let calcType = null;
      let plTokens = [];
      let budgetTokens = [];
      if (plCalculation && (arrayHasItems(plCalculation.tokens) || arrayHasItems(plCalculation))) {
        calcType = 'pl';
        plTokens = normalizePLTokens({ tokens: arrayHasItems(plCalculation.tokens) ? plCalculation.tokens : plCalculation, plKeysById });
      }
      if (budgetCalculation && (arrayHasItems(budgetCalculation.tokens) || arrayHasItems(budgetCalculation))) {
        if (!calcType) calcType = 'budget';
        budgetTokens = normalizeBudgetTokens({
          tokens: arrayHasItems(budgetCalculation.tokens) ? budgetCalculation.tokens : budgetCalculation,
          budgetItemsByCode,
          budgetCategoriesById,
        });
      }
      if (!calcType && plTokens.length > 0) {
        calcType = 'pl';
      } else if (!calcType && budgetTokens.length > 0) {
        calcType = 'budget';
      }

      return {
        name: safeString(itemData.name || ''),
        is_percentage: Boolean(itemData.is_percentage),
        calculationType: calcType,
        plTokens,
        budgetTokens,
      };
    }
    return { name: '', is_percentage: false, calculationType: null, plTokens: [], budgetTokens: [] };
  }, [mode, item]);

  const [localState, setLocalState] = useState(buildInitialState);

  useEffect(() => {
    if (!isOpen) {
      setLocalState({ name: '', is_percentage: false, calculationType: 'pl', plTokens: [], budgetTokens: [] });
      setCalculationType('pl');
      setPlSearch('');
      setBudgetLineItemSearch('');
      setIsPreviousYearEnabled(false);
      setPasteError('');
      initializedItemIdRef.current = null;
      return;
    }
    
    // Only initialize once per item/mode change
    const currentItemId = mode === 'edit' && item ? item.id : 'create';
    if (initializedItemIdRef.current === currentItemId) {
      return; // Already initialized for this item
    }
    
    // Build initial state when opening
    let initialState;
    if (mode === 'edit' && item) {
      const itemData = item?.item || {};
      const plCalculation = item?.calculation_profit_and_loss;
      const budgetCalculation = item?.calculation_budget;

      let calcType = null;
      let plTokens = [];
      let budgetTokens = [];
      if (plCalculation && (arrayHasItems(plCalculation.tokens) || arrayHasItems(plCalculation))) {
        calcType = 'pl';
        plTokens = normalizePLTokens({ tokens: arrayHasItems(plCalculation.tokens) ? plCalculation.tokens : plCalculation, plKeysById });
      }
      if (budgetCalculation && (arrayHasItems(budgetCalculation.tokens) || arrayHasItems(budgetCalculation))) {
        if (!calcType) calcType = 'budget';
        budgetTokens = normalizeBudgetTokens({
          tokens: arrayHasItems(budgetCalculation.tokens) ? budgetCalculation.tokens : budgetCalculation,
          budgetItemsByCode,
          budgetCategoriesById,
        });
      }
      if (!calcType && plTokens.length > 0) {
        calcType = 'pl';
      } else if (!calcType && budgetTokens.length > 0) {
        calcType = 'budget';
      } else if (!calcType) {
        calcType = 'pl'; // Default to P&L if no calculation exists
      }

      initialState = {
        name: safeString(itemData.name || ''),
        is_percentage: Boolean(itemData.is_percentage),
        calculationType: calcType,
        plTokens,
        budgetTokens,
      };
    } else {
      initialState = { name: '', is_percentage: false, calculationType: 'pl', plTokens: [], budgetTokens: [] };
    }
    
    setLocalState(initialState);
    setCalculationType(initialState.calculationType);
    setIsPreviousYearEnabled(false);
    setPasteError('');
    initialSnapshotRef.current = JSON.stringify(initialState);
    initializedItemIdRef.current = currentItemId;
    const initialTokens =
      initialState.calculationType === 'pl'
        ? initialState.plTokens
        : initialState.calculationType === 'budget'
          ? initialState.budgetTokens
          : [];
    setCursorPosition(arrayHasItems(initialTokens) ? initialTokens.length : 0);
  }, [isOpen, mode, item?.id]);

  const hasChanges = useMemo(() => {
    if (!isOpen) return false;
    const snap = initialSnapshotRef.current || '';
    return JSON.stringify(localState) !== snap;
  }, [isOpen, localState]);

  const activeTokens = useMemo(() => {
    if (calculationType === 'pl') return arrayHasItems(localState?.plTokens) ? localState.plTokens : [];
    if (calculationType === 'budget') return arrayHasItems(localState?.budgetTokens) ? localState.budgetTokens : [];
    return [];
  }, [calculationType, localState?.plTokens, localState?.budgetTokens]);

  const getInvalidPLItems = useMemo(() => {
    if (calculationType !== 'pl') return [];
    const invalid = [];
    const arr = activeTokens;
    for (const t of arr) {
      if (t?.type === 'pl_key') {
        const plId = safeString(t.plId ?? t.pl_id).trim();
        if (plId && !activePlKeyIds.has(plId)) {
          invalid.push(plId);
        }
      }
    }
    return [...new Set(invalid)];
  }, [activePlKeyIds, activeTokens, calculationType]);

  const validation = useMemo(() => {
    if (!calculationType || activeTokens.length === 0) {
      return { isValid: true, reason: '', invalidCodes: [] };
    }
    return validateTokens(activeTokens, calculationType, budgetItemsByCode);
  }, [activeTokens, calculationType, budgetItemsByCode]);

  const canSave = useMemo(() => {
    const nameOk = safeString(localState?.name).trim().length > 0;
    const tokensOk = calculationType ? activeTokens.length > 0 : true;
    return nameOk && tokensOk && validation.isValid && hasChanges && !isSaving;
  }, [localState?.name, activeTokens, calculationType, validation.isValid, hasChanges, isSaving]);

  const lastTokenKind = useMemo(() => {
    const arr = activeTokens;
    return arr.length ? getTokenKind(arr[arr.length - 1]) : 'start';
  }, [activeTokens]);

  const prevTokenKind = useMemo(() => {
    if (cursorPosition <= 0 || cursorPosition > activeTokens.length) return 'start';
    return getTokenKind(activeTokens[cursorPosition - 1]);
  }, [activeTokens, cursorPosition]);

  const nextTokenKind = useMemo(() => {
    if (cursorPosition < 0 || cursorPosition >= activeTokens.length) return 'end';
    return getTokenKind(activeTokens[cursorPosition]);
  }, [activeTokens, cursorPosition]);

  const parenBalance = useMemo(() => {
    const arr = activeTokens;
    let b = 0;
    for (const t of arr) {
      const kind = getTokenKind(t);
      if (kind === 'lparen') b += 1;
      if (kind === 'rparen') b -= 1;
    }
    return b;
  }, [activeTokens]);

  const getInvalidCodes = useMemo(() => {
    if (calculationType !== 'budget') return [];
    const invalid = [];
    const arr = activeTokens;
    for (const t of arr) {
      if (t?.type === 'budget_code') {
        const code = safeString(t.code).trim();
        if (!budgetItemsByCode[code]) {
          invalid.push(code);
        }
      }
    }
    return [...new Set(invalid)];
  }, [activeTokens, calculationType, budgetItemsByCode]);

  const getInvalidCategories = useMemo(() => {
    if (calculationType !== 'budget') return [];
    const invalid = [];
    const arr = activeTokens;
    for (const t of arr) {
      if (t?.type === 'budget_category') {
        const categoryId = t.categoryId ?? t.category_id ?? null;
        if (categoryId && !budgetCategoriesById[String(categoryId)]) {
          invalid.push(String(categoryId));
        }
      }
    }
    return [...new Set(invalid)];
  }, [activeTokens, calculationType, budgetCategoriesById]);

  const focusFormulaArea = useCallback(() => {
    textareaRef.current?.focus?.();
  }, []);

  const replaceActiveTokens = useCallback((updater) => {
    setLocalState((prev) => {
      const currentTokens = calculationType === 'pl' ? prev.plTokens : prev.budgetTokens;
      const nextTokens = typeof updater === 'function' ? updater(arrayHasItems(currentTokens) ? currentTokens : []) : updater;
      return {
        ...prev,
        ...(calculationType === 'pl' ? { plTokens: nextTokens } : { budgetTokens: nextTokens }),
      };
    });
  }, [calculationType]);

  const canInsertValueAtCursor = useCallback(() => {
    return prevTokenKind !== 'value' && prevTokenKind !== 'rparen' && nextTokenKind !== 'value' && nextTokenKind !== 'lparen';
  }, [prevTokenKind, nextTokenKind]);

  const canInsertOperatorAtCursor = useCallback((operatorValue) => {
    if (operatorValue === ')') {
      return parenBalance > 0 && (prevTokenKind === 'value' || prevTokenKind === 'rparen');
    }
    if (operatorValue === '(') {
      return prevTokenKind !== 'value' && prevTokenKind !== 'rparen';
    }
    if (operatorValue === '-') {
      return (
        prevTokenKind === 'start' ||
        prevTokenKind === 'op' ||
        prevTokenKind === 'lparen' ||
        prevTokenKind === 'value' ||
        prevTokenKind === 'rparen'
      );
    }
    return prevTokenKind === 'value' || prevTokenKind === 'rparen';
  }, [parenBalance, prevTokenKind]);

  const insertAtCursor = (newToken) => {
    const tokens = [...activeTokens];
    tokens.splice(cursorPosition, 0, newToken);
    replaceActiveTokens(tokens);
    setCursorPosition((prev) => prev + 1);
    focusFormulaArea();
  };

  const insertTokensAtCursor = useCallback((newTokens) => {
    if (!arrayHasItems(newTokens)) return;
    const tokens = [...activeTokens];
    tokens.splice(cursorPosition, 0, ...newTokens);
    replaceActiveTokens(tokens);
    setCursorPosition((prev) => prev + newTokens.length);
    setPasteError('');
    focusFormulaArea();
  }, [activeTokens, cursorPosition, focusFormulaArea, replaceActiveTokens]);

  const insertNumericCharacter = useCallback((char) => {
    const value = safeString(char);
    if (!value) return;

    if (cursorPosition > 0 && isNumberToken(activeTokens[cursorPosition - 1])) {
      replaceActiveTokens((currentTokens) => {
        const tokens = [...currentTokens];
        const current = safeString(tokens[cursorPosition - 1]?.value);
        if (value === '.' && current.includes('.')) return currentTokens;
        tokens[cursorPosition - 1] = { ...tokens[cursorPosition - 1], value: `${current}${value}` };
        return tokens;
      });
      focusFormulaArea();
      return;
    }

    if (cursorPosition < activeTokens.length && isNumberToken(activeTokens[cursorPosition])) {
      replaceActiveTokens((currentTokens) => {
        const tokens = [...currentTokens];
        const current = safeString(tokens[cursorPosition]?.value);
        if (value === '.' && current.includes('.')) return currentTokens;
        tokens[cursorPosition] = { ...tokens[cursorPosition], value: `${value}${current}` };
        return tokens;
      });
      focusFormulaArea();
      return;
    }

    if (!canInsertValueAtCursor()) return;
    insertAtCursor({ type: 'number', value: value === '.' ? '0.' : value });
  }, [activeTokens, canInsertValueAtCursor, cursorPosition, focusFormulaArea, insertAtCursor, replaceActiveTokens]);

  const deleteNumberCharacter = useCallback((index, direction) => {
    replaceActiveTokens((currentTokens) => {
      const tokens = [...currentTokens];
      const token = tokens[index];
      if (!isNumberToken(token)) return currentTokens;
      const currentValue = safeString(token.value);
      const nextValue =
        direction === 'backspace'
          ? currentValue.slice(0, -1)
          : currentValue.slice(1);

      if (!nextValue || nextValue === '-') {
        tokens.splice(index, 1);
      } else {
        tokens[index] = { ...token, value: nextValue };
      }
      return tokens;
    });
  }, [replaceActiveTokens]);

  const addPLKeyToken = (plKey) => {
    if (!plKey?.pl_id) return;
    if (!canInsertValueAtCursor()) return;
    insertAtCursor({
      type: 'pl_key',
      plId: plKey.pl_id,
      plName: plKey.name || '',
      is_previous_year: isPreviousYearEnabled,
    });
  };

  const addBudgetCodeToken = (code, name) => {
    if (!code) return;
    if (!canInsertValueAtCursor()) return;
    insertAtCursor({
      type: 'budget_code',
      code: safeString(code).trim(),
      name: safeString(name).trim(),
      is_previous_year: isPreviousYearEnabled,
    });
  };

  const addBudgetCategoryToken = (category) => {
    if (!category?.id) return;
    if (!canInsertValueAtCursor()) return;
    insertAtCursor({
      type: 'budget_category',
      categoryId: category.id,
      categoryName: category.name || '',
      is_previous_year: isPreviousYearEnabled,
    });
  };

  const addOperatorToken = (op) => {
    const value = safeString(op).trim();
    if (!value) return;
    if (!isMathOperator(value) && value !== '(' && value !== ')') return;
    if (!canInsertOperatorAtCursor(value)) return;
    insertAtCursor({ type: 'operator', value });
  };

  const deleteToken = (index) => {
    replaceActiveTokens((currentTokens) => {
      const tokens = [...currentTokens];
      if (index < 0 || index >= tokens.length) return currentTokens;
      tokens.splice(index, 1);
      return tokens;
    });
    if (index < cursorPosition) {
      setCursorPosition((prev) => Math.max(0, prev - 1));
    }
    focusFormulaArea();
  };

  const clearTokens = () => {
    replaceActiveTokens([]);
    setCursorPosition(0);
    setPasteError('');
    focusFormulaArea();
  };

  const handleCalculationTypeChange = (type) => {
    setCalculationType(type);
    setLocalState((prev) => ({ ...prev, calculationType: type }));
    if (type !== 'pl') {
      setIsPreviousYearEnabled(false);
    }
    setPasteError('');
    const nextTokens =
      type === 'pl'
        ? localState?.plTokens
        : type === 'budget'
          ? localState?.budgetTokens
          : [];
    setCursorPosition(arrayHasItems(nextTokens) ? nextTokens.length : 0);
  };

  const formulaText = useMemo(() => {
    const tokens = activeTokens;
    return tokens.map((t) => tokenToText(t, budgetItemsByCode)).filter(Boolean).join(' ');
  }, [activeTokens, budgetItemsByCode]);

  const parseFormulaText = useCallback((value) => {
    const text = safeString(value);
    const candidates = calculationType === 'pl' ? plPasteCandidates : budgetPasteCandidates;
    const tokens = [];
    let index = 0;

    while (index < text.length) {
      const currentChar = text[index];
      const remainingText = text.slice(index);
      const remainingLower = remainingText.toLowerCase();

      if (/\s/.test(currentChar)) {
        index += 1;
        continue;
      }

      const matchedCandidate = candidates.find((candidate) => {
        const candidateLabel = safeString(candidate.label).trim();
        if (!candidateLabel) return false;
        if (!remainingLower.startsWith(candidateLabel.toLowerCase())) return false;
        return isFormulaBoundaryCharacter(remainingText[candidateLabel.length]);
      });

      if (matchedCandidate) {
        tokens.push({ ...matchedCandidate.token });
        index += matchedCandidate.label.length;
        continue;
      }

      if (['+', '-', '*', '/', '(', ')'].includes(currentChar)) {
        tokens.push({ type: 'operator', value: currentChar });
        index += 1;
        continue;
      }

      const numberMatch = text.slice(index).match(/^(?:\d+\.\d+|\d+|\.\d+)/);
      if (numberMatch) {
        tokens.push({
          type: 'number',
          value: numberMatch[0].startsWith('.') ? `0${numberMatch[0]}` : numberMatch[0],
        });
        index += numberMatch[0].length;
        continue;
      }

      let endIndex = index;
      while (endIndex < text.length && !isFormulaBoundaryCharacter(text[endIndex])) {
        endIndex += 1;
      }

      const rawValue = safeString(text.slice(index, endIndex)).trim();
      if (!rawValue) {
        index += 1;
        continue;
      }

      if (calculationType === 'budget') {
        tokens.push({ type: 'budget_code', code: rawValue, name: rawValue });
        index = endIndex;
        continue;
      }

      return { tokens: [], error: `Could not parse "${rawValue}" in the formula.` };
    }

    return { tokens, error: '' };
  }, [budgetPasteCandidates, calculationType, plPasteCandidates]);

  const handleCopyFormula = useCallback(async () => {
    if (!formulaText) return;
    try {
      await navigator.clipboard.writeText(formulaText);
      setPasteError('');
      toast.success('Formula copied to clipboard', { position: 'bottom-right' });
    } catch (error) {
      setPasteError('Could not copy the formula to the clipboard.');
      toast.error('Could not copy the formula to the clipboard.', { position: 'bottom-right' });
    }
  }, [formulaText]);

  const handleFormulaPaste = useCallback((e) => {
    if (!calculationType) return;
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || '';
    if (!safeString(pastedText).trim()) return;

    const { tokens, error } = parseFormulaText(pastedText);
    if (error) {
      setPasteError(error);
      return;
    }

    insertTokensAtCursor(tokens);
  }, [calculationType, insertTokensAtCursor, parseFormulaText]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    const name = safeString(localState?.name).trim();
    const is_percentage = Boolean(localState?.is_percentage);
    const itemData = { name, is_percentage };

    let calculation_profit_and_loss = undefined;
    let calculation_budget = undefined;

    if (arrayHasItems(localState.plTokens) && localState.plTokens.length > 0) {
      calculation_profit_and_loss = { tokens: localState.plTokens };
    }
    if (arrayHasItems(localState.budgetTokens) && localState.budgetTokens.length > 0) {
      calculation_budget = { tokens: localState.budgetTokens };
    }
    
    // If editing and we had a calculation before but now cleared it, set to null
    if (mode === 'edit' && item) {
      const hadPLCalc = item?.calculation_profit_and_loss;
      const hadBudgetCalc = item?.calculation_budget;
      // If we had a PL calc but now don't have one, clear it
      if (hadPLCalc && calculationType !== 'pl' && calculation_profit_and_loss === undefined) {
        calculation_profit_and_loss = null;
      }
      // If we had a budget calc but now don't have one, clear it
      if (hadBudgetCalc && calculationType !== 'budget' && calculation_budget === undefined) {
        calculation_budget = null;
      }
    }

    await onSave?.({ item: itemData, calculation_profit_and_loss, calculation_budget });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSave, localState?.name, localState?.is_percentage, JSON.stringify(localState?.plTokens), JSON.stringify(localState?.budgetTokens), calculationType, mode, item]);

  useEffect(() => {
    submitRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!onRegisterSubmit) return;
    onRegisterSubmit(() => submitRef.current?.());
  }, [onRegisterSubmit]);

  useEffect(() => {
    onCanSaveChange?.(canSave);
  }, [canSave, onCanSaveChange]);

  const handleTokenClick = (index) => {
    setCursorPosition(index + 1);
    setPasteError('');
    focusFormulaArea();
  };

  const handleFormulaKeyDown = (e) => {
    if (!calculationType) return;
    setPasteError('');
    if (isDigitKey(e.key) || e.key === '.') {
      e.preventDefault();
      insertNumericCharacter(e.key);
      return;
    }
    if (['+', '-', '*', '/', '(', ')'].includes(e.key)) {
      e.preventDefault();
      addOperatorToken(e.key);
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (cursorPosition > 0) {
        if (isNumberToken(activeTokens[cursorPosition - 1])) {
          deleteNumberCharacter(cursorPosition - 1, 'backspace');
          return;
        }
        deleteToken(cursorPosition - 1);
      }
      return;
    }
    if (e.key === 'Delete') {
      e.preventDefault();
      if (cursorPosition < activeTokens.length) {
        if (isNumberToken(activeTokens[cursorPosition])) {
          deleteNumberCharacter(cursorPosition, 'delete');
          return;
        }
        deleteToken(cursorPosition);
      }
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setCursorPosition((prev) => Math.max(0, prev - 1));
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setCursorPosition((prev) => Math.min(activeTokens.length, prev + 1));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setCursorPosition(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setCursorPosition(activeTokens.length);
      return;
    }
  };

  const content = (
    <div className="px-0 py-4">
      <div className="mb-5 rounded-xl border border-gray-200 bg-gradient-to-r from-slate-50 to-white p-4 shadow-sm">
        <div className="text-sm font-semibold text-gray-900">Formula Builder</div>
        <p className="mt-1 text-sm text-gray-500">
          Build a display row using P&amp;L items, budget categories, budget line items, and operators.
        </p>
      </div>

      <div className={sectionCardClassName}>
        <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
        <input
          type="text"
          value={localState?.name || ''}
          onChange={(e) => setLocalState((p) => ({ ...p, name: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter item name"
          disabled={isSaving}
        />
        <label className="mt-4 flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <input
            type="checkbox"
            checked={localState?.is_percentage || false}
            onChange={(e) => setLocalState((p) => ({ ...p, is_percentage: e.target.checked }))}
            disabled={isSaving}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:opacity-50"
          />
          <span className="ml-2 text-sm text-gray-700">Show this value as percentage</span>
        </label>
      </div>

      <div className={`${sectionCardClassName} mt-4 mb-4`}>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => handleCalculationTypeChange('pl')}
            className={`px-4 py-2 border rounded-lg transition-colors text-sm font-medium shadow-sm ${
              calculationType === 'pl'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            disabled={isSaving}
          >
            P&L Calculation
          </button>
          <button
            type="button"
            onClick={() => handleCalculationTypeChange('budget')}
            className={`px-4 py-2 border rounded-lg transition-colors text-sm font-medium shadow-sm ${
              calculationType === 'budget'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            disabled={isSaving}
          >
            Budget Calculation
          </button>
        </div>
      </div>

      {calculationType && (
        <>

          {calculationType === 'pl' && getInvalidPLItems.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Missing P&amp;L Data</p>
                  <p className="text-xs">{getProfitAndLossWarningMessage(getInvalidPLItems.length)}</p>
                </div>
              </div>
            </div>
          )}

          {calculationType === 'budget' && (getInvalidCodes.length > 0 || getInvalidCategories.length > 0) && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Missing Budget Items</p>
                  <p className="text-xs">
                    {getInvalidCodes.length > 0 && getInvalidCategories.length > 0
                      ? 'Budget codes and categories in red with triangle icon do not exist and will be treated as 0.'
                      : getInvalidCodes.length > 0
                      ? 'Budget codes in red with triangle icon do not exist in any budget and will be treated as 0.'
                      : 'Budget categories in red with triangle icon do not exist and will be treated as 0.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className={`${sectionCardClassName} mb-6`}>
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Formula</label>
                <span className="text-xs text-gray-400">
                  Use arrow keys to move the cursor, `Backspace` and `Delete` to remove tokens
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopyFormula}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSaving || !formulaText}
                title="Copy formula"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Copy
              </button>
            </div>
            <div className="relative">
              <div
                ref={textareaRef}
                tabIndex={0}
                onClick={focusFormulaArea}
                onKeyDown={handleFormulaKeyDown}
                onPaste={handleFormulaPaste}
                className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm font-mono min-h-[120px] resize-y bg-gradient-to-b from-white to-gray-50 shadow-inner outline-none focus:ring-2 focus:ring-blue-200"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {activeTokens.length > 0 ? (
                  <>
                    {activeTokens.map((t, index) => {
                      const text = tokenToText(t, budgetItemsByCode);
                      const isInvalidPL = calculationType === 'pl' && t.type === 'pl_key' && getInvalidPLItems.includes(safeString(t.plId ?? t.pl_id).trim());
                      const isInvalidCode = calculationType === 'budget' && t.type === 'budget_code' && getInvalidCodes.includes(safeString(t.code).trim());
                      const isInvalidCategory = calculationType === 'budget' && t.type === 'budget_category' && getInvalidCategories.includes(String(t.categoryId ?? t.category_id ?? ''));
                      const isInvalid = isInvalidPL || isInvalidCode || isInvalidCategory;
                      return (
                        <span key={`token-${index}`} className="inline-flex items-center gap-1">
                          {cursorPosition === index && (
                            <span className="inline-block w-0.5 h-4 bg-blue-500 animate-pulse" />
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTokenClick(index);
                            }}
                            className={`text-left px-1.5 py-0.5 rounded transition-colors hover:opacity-80 ${getTextareaTokenClasses(t, isInvalid)}`}
                            title={isInvalidPL ? 'This P&L item has no current data and will be treated as 0.' : undefined}
                          >
                            {text}
                          </button>
                          {isInvalid && (
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-600 inline" />
                          )}
                          {index < activeTokens.length - 1 && <span> </span>}
                        </span>
                      );
                    })}
                    {cursorPosition === activeTokens.length && (
                      <span className="inline-block w-0.5 h-4 bg-blue-500 ml-1 animate-pulse" />
                    )}
                  </>
                ) : (
                  <span className="text-gray-400">Click items and operators below to build your formula...</span>
                )}
              </div>
              {activeTokens.length > 0 && (
                <button
                  type="button"
                  onClick={clearTokens}
                  className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
                  title="Clear formula"
                  disabled={isSaving}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {activeTokens.length > 0 && (
              <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Token List</div>
                <div className="flex flex-wrap gap-2">
                {activeTokens.map((t, index) => {
                  const isInvalidPL = calculationType === 'pl' && t.type === 'pl_key' && getInvalidPLItems.includes(safeString(t.plId ?? t.pl_id).trim());
                  const isInvalidCode = calculationType === 'budget' && t.type === 'budget_code' && getInvalidCodes.includes(safeString(t.code).trim());
                  const isInvalidCategory = calculationType === 'budget' && t.type === 'budget_category' && getInvalidCategories.includes(String(t.categoryId ?? t.category_id ?? ''));
                  const isInvalid = isInvalidPL || isInvalidCode || isInvalidCategory;
                  return (
                    <div
                      key={`${t.type}-${t.plId ?? t.code ?? t.value}-${index}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${
                        isInvalid ? 'bg-red-100 border border-red-300' : 'bg-gray-100'
                      }`}
                    >
                      <span>{tokenToText(t, budgetItemsByCode)}</span>
                      {isInvalid && <ExclamationTriangleIcon className="h-3 w-3 text-red-600" />}
                      <button
                        type="button"
                        onClick={() => deleteToken(index)}
                        className="text-gray-500 hover:text-red-600"
                        title="Remove from formula"
                        disabled={isSaving}
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
                </div>
              </div>
            )}

            {!validation.isValid && activeTokens.length > 0 && (
              <div className="mt-2 text-sm text-red-600">{validation.reason}</div>
            )}
            {pasteError && (
              <div className="mt-2 text-sm text-red-600">{pasteError}</div>
            )}
          </div>
          {calculationType === 'pl' && (
            <div className={`${sectionCardClassName} mb-4`}>
              <label className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <input
                  type="checkbox"
                  checked={isPreviousYearEnabled}
                  onChange={(e) => setIsPreviousYearEnabled(e.target.checked)}
                  disabled={isSaving || calculationType !== 'pl'}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Previous year</span>
              </label>
              <p className="mt-2 text-xs text-gray-500">
                When enabled, newly added P&amp;L items will be marked as previous year.
              </p>
            </div>
          )}
          {calculationType === 'pl' && (
            <>
              <div className={sectionCardClassName}>
                <label className="block text-sm font-medium text-gray-700 mb-2">P&L Items</label>
                <input
                  type="text"
                  value={plSearch}
                  onChange={(e) => setPlSearch(e.target.value)}
                  className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search P&L items"
                  disabled={isSaving}
                />
                <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                  {filteredProfitAndLossKeys.map((plKey) => (
                    <button
                      key={plKey.pl_id}
                      type="button"
                      onClick={() => addPLKeyToken(plKey)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors text-sm font-medium ${
                        plKey?.is_new
                          ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                          : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                      }`}
                      disabled={isSaving || !canInsertValueAtCursor()}
                      title={!plKey?.is_new ? 'This P&L item has no current data and will be treated as 0.' : undefined}
                    >
                      {!plKey?.is_new && <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />}
                      {appendPreviousYearLabel(plKey.name || plKey.pl_id, isPreviousYearEnabled)}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {calculationType === 'budget' && (
            <>
              <div className={sectionCardClassName}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Budget Categories</label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto rounded-lg border border-purple-100 bg-purple-50/40 p-3">
                  {(arrayHasItems(categoriesToRender) ? categoriesToRender : []).map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => addBudgetCategoryToken(category)}
                      className="px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors text-sm font-medium"
                      disabled={isSaving || !canInsertValueAtCursor()}
                    >
                      {appendPreviousYearLabel(category.name, isPreviousYearEnabled)}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`${sectionCardClassName} mt-4`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Budget Line Items</label>
                <input
                  type="text"
                  value={budgetLineItemSearch}
                  onChange={(e) => setBudgetLineItemSearch(e.target.value)}
                  className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search budget line items"
                  disabled={isSaving}
                />
                <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto rounded-lg border border-green-100 bg-green-50/40 p-3">
                  {filteredBudgetLineItems.map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => addBudgetCodeToken(item.code, item.name)}
                      className="px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors text-sm font-medium"
                      disabled={isSaving || !canInsertValueAtCursor()}
                    >
                      {appendPreviousYearLabel(formatBudgetCodeLabel(item.code, item.name), isPreviousYearEnabled)}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className={`${sectionCardClassName} mt-4`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Operators</label>
            <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              {['+', '-', '*', '/', '(', ')'].map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => addOperatorToken(op)}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-lg font-semibold shadow-sm"
                  disabled={
                    isSaving ||
                    !canInsertOperatorAtCursor(op)
                  }
                >
                  {op}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div>
      {content}
      <div className="flex justify-end gap-2 px-0 pb-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};
